import { useState, useEffect } from 'react';
import { Folder } from 'lucide-react';
import Modal from './Modal';

export default function ProjectModal({ isOpen, onClose, onSave, editItem = null, goals = [] }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goalId: '',
    dueDate: '',
    status: 'active'
  });

  useEffect(() => {
    if (editItem) {
      setFormData({
        name: editItem.name || '',
        description: editItem.description || '',
        goalId: editItem.goalId || '',
        dueDate: editItem.dueDate || '',
        status: editItem.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        goalId: '',
        dueDate: '',
        status: 'active'
      });
    }
  }, [editItem, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      goalId: formData.goalId || null
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? 'Edit Project' : 'New Project'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Project Name
          </label>
          <input
            type="text"
            placeholder="e.g., Launch new product line"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            autoFocus
            className="w-full py-2 px-3 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Description (optional)
          </label>
          <textarea
            placeholder="What is the desired outcome of this project?"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            className="w-full py-2 px-3 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted resize-none"
          />
        </div>

        {/* Link to Goal */}
        {goals.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Link to Goal (optional)
            </label>
            <select
              value={formData.goalId}
              onChange={(e) => setFormData({ ...formData, goalId: e.target.value })}
              className="w-full py-2 px-3 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all cursor-pointer"
            >
              <option value="">No goal (standalone project)</option>
              {goals.map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Due Date */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Target Completion Date (optional)
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            className="w-full py-2 px-3 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 bg-surface-hover text-text-secondary border border-border rounded-xl text-sm font-medium cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-2.5 px-4 bg-gradient-to-r from-accent to-orange-hover text-surface-base rounded-xl text-sm font-semibold cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            {editItem ? 'Save Changes' : 'Create Project'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
